export type ColorTheme = 'orange' | 'red' | 'green' | 'blue' | 'purple' | 'cream';
export type AccessibilityMode = 'normal' | 'colorBlind' | 'highContrast';
export type TextSize = 'small' | 'medium' | 'large' | 'extraLarge';
export type AppearanceMode = 'light' | 'dark' | 'system';

export interface ColorThemeConfig {
  name: string;
  description: string;
  primary: string;
  secondary: string;
  accent: string;
  success: string;
  warning: string;
  error: string;
}

export const ColorThemes: Record<ColorTheme, ColorThemeConfig> = {
  orange: {
    name: 'Orange',
    description: 'Warm and energetic orange theme',
    primary: '#ff6b35',
    secondary: '#f7931e',
    accent: '#ff8c42',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  },
  red: {
    name: 'Red',
    description: 'Bold and passionate red theme',
    primary: '#dc2626',
    secondary: '#b91c1c',
    accent: '#ef4444',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#dc2626',
  },
  green: {
    name: 'Green',
    description: 'Fresh and natural green theme',
    primary: '#059669',
    secondary: '#047857',
    accent: '#10b981',
    success: '#059669',
    warning: '#f59e0b',
    error: '#ef4444',
  },
  blue: {
    name: 'Blue',
    description: 'Professional and trustworthy blue theme',
    primary: '#2563eb',
    secondary: '#1d4ed8',
    accent: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  },
  purple: {
    name: 'Purple',
    description: 'Creative and luxurious purple theme',
    primary: '#7c3aed',
    secondary: '#6d28d9',
    accent: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  },
  cream: {
    name: 'Cream',
    description: 'Soft and elegant cream theme',
    primary: '#d97706',
    secondary: '#b45309',
    accent: '#f59e0b',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  },
};

export const TextSizeMultipliers: Record<TextSize, number> = {
  small: 0.875,
  medium: 1,
  large: 1.125,
  extraLarge: 1.25,
};

export interface ThemeConfig {
  colorTheme: ColorTheme;
  accessibilityMode: AccessibilityMode;
  textSize: TextSize;
  appearanceMode: AppearanceMode;
  isDarkMode: boolean;
}

export const defaultThemeConfig: ThemeConfig = {
  colorTheme: 'orange',
  accessibilityMode: 'normal',
  textSize: 'medium',
  appearanceMode: 'system',
  isDarkMode: false,
};

export function generateTheme(config: ThemeConfig) {
  const colorTheme = ColorThemes[config.colorTheme];
  const textMultiplier = TextSizeMultipliers[config.textSize];
  
  // Apply accessibility adjustments
  let adjustedColors = { ...colorTheme };
  
  if (config.accessibilityMode === 'colorBlind') {
    // High contrast colors for color blind users
    adjustedColors = {
      ...adjustedColors,
      primary: '#000000',
      secondary: '#333333',
      accent: '#666666',
    };
  } else if (config.accessibilityMode === 'highContrast') {
    // Maximum contrast
    adjustedColors = {
      ...adjustedColors,
      primary: '#000000',
      secondary: '#ffffff',
      accent: '#000000',
    };
  }

  return {
    colors: adjustedColors,
    textSize: textMultiplier,
    isDarkMode: config.isDarkMode,
    accessibilityMode: config.accessibilityMode,
  };
}

export const ThemePresets = {
  default: defaultThemeConfig,
  highContrast: {
    ...defaultThemeConfig,
    accessibilityMode: 'highContrast' as const,
  },
  colorBlind: {
    ...defaultThemeConfig,
    accessibilityMode: 'colorBlind' as const,
  },
  largeText: {
    ...defaultThemeConfig,
    textSize: 'large' as const,
  },
  extraLargeText: {
    ...defaultThemeConfig,
    textSize: 'extraLarge' as const,
  },
};
